import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import eventStructureQueryKeys from "../query-keys/event-structure.query-keys";
import { UpdateEventStructureTicketAlertForm } from "../schemas/event-structure/update-event-structure.schema";

interface UpdateEventStructureTicketAlertArgs {
  eventId: string;
  structureId: string;
  data: UpdateEventStructureTicketAlertForm;
}

async function updateEventStructureTicketAlert({
  eventId,
  structureId,
  data,
}: UpdateEventStructureTicketAlertArgs) {
  try {
    const { data: response } = await privateAxios.patch(
      `/event-structure/event/${eventId}/structure/${structureId}`,
      data
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
}

interface UseUpdateEventStructureTicketAlertArgs {
  eventId: string;
  structureId: string;
}

export const useUpdateEventStructureTicketAlertMutation = ({
  eventId,
  structureId,
}: UseUpdateEventStructureTicketAlertArgs) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: UpdateEventStructureTicketAlertForm }) =>
      updateEventStructureTicketAlert({ eventId, structureId, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventStructureQueryKeys.get({ eventId, structureId }),
      });
    },
  });
};

interface UpdateEventStructureTicketAlertByIdArgs {
  id: string;
  data: UpdateEventStructureTicketAlertForm;
}

async function updateEventStructureTicketAlertById({
  id,
  data,
}: UpdateEventStructureTicketAlertByIdArgs) {
  try {
    const { data: response } = await privateAxios.patch(
      `/event-structure/${id}`,
      data
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
}

interface UseUpdateEventStructureTicketAlertByIdArgs {
  id: string;
}

export const useUpdateEventStructureTicketAlertByIdMutation = ({
  id,
}: UseUpdateEventStructureTicketAlertByIdArgs) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: UpdateEventStructureTicketAlertForm }) =>
      updateEventStructureTicketAlertById({ id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventStructureQueryKeys.get({ id }),
      });
    },
  });
};
